import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { protectSheetWithRandomPassword } from "../ExcelUtils";

const C = {
    HEADER_BG:      "1F4E79",
    GROUP_BG:       "2E75B6",
    GROUP_LABEL_BG: "D6E4F0",
    ALT_ROW:        "EBF3FB",
    SUMMARY_BG:     "D9E1F2",
    WHITE:          "FFFFFF",
    DARK_TEXT:      "1A1A2E",
};

const BORDER = {
    top:    { style: "thin" },
    bottom: { style: "thin" },
    left:   { style: "thin" },
    right:  { style: "thin" },
};

const DOUBLE_BOTTOM = {
    ...BORDER,
    bottom: { style: "double" },
};

function applyBorder(cell, borderDef = BORDER) {
    cell.border = borderDef;
}

function applyCurrency(cell) {
    cell.numFmt = "#,##0.00";
    cell.alignment = { horizontal: "right", vertical: "middle" };
}

function formatDate(val) {
    if (!val) return "";
    return new Date(val).toLocaleDateString("en-GB", {
        year:  "numeric",
        month: "2-digit",
        day:   "2-digit",
    });
}

export async function exportLedgerTrailBalance({
    data = [],
    branchName = "",
    selectedDate = null,
    loginPlaceId = "",
}) {
    if (!data || data.length === 0) return;

    const workbook  = new ExcelJS.Workbook();
    const sheet     = workbook.addWorksheet("Trial Balance");
    const COL_COUNT = 4;

    // column widths
    sheet.columns = [
        { width: 110 },  // A – Account name
        { width: 18 },   // B – Code (subAcNumber)
        { width: 18 },   // C – Debit
        { width: 18 },   // D – Credit
    ];

    const mergeRow = (rowNum) =>
        sheet.mergeCells(rowNum, 1, rowNum, COL_COUNT);

    // ── ROW 1: BANK TITLE ──────────────────────────────────
    const titleRow = sheet.addRow(["Samurdhi Bank"]);
    mergeRow(titleRow.number);
    titleRow.font      = { bold: true, size: 16, color: { argb: C.WHITE } };
    titleRow.alignment = { horizontal: "center", vertical: "middle" };
    titleRow.height    = 28;
    titleRow.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: C.HEADER_BG } };

    // ── ROW 2: BRANCH ──────────────────────────────────────
    const branchRow = sheet.addRow([`Branch : ${branchName}`]);
    mergeRow(branchRow.number);
    branchRow.font      = { bold: true, size: 12, color: { argb: C.WHITE } };
    branchRow.alignment = { horizontal: "left", vertical: "middle", indent: 1 };
    branchRow.height    = 20;
    branchRow.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: C.HEADER_BG } };

    // ── ROW 3: REPORT NAME ─────────────────────────────────
    const reportName = `Trial Balance of ${branchName} branch`;
    const nameRow    = sheet.addRow([reportName]);
    mergeRow(nameRow.number);
    nameRow.font      = { bold: true, size: 12, underline: true, color: { argb: C.DARK_TEXT } };
    nameRow.alignment = { horizontal: "center", vertical: "middle" };
    nameRow.height    = 20;
    nameRow.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "DDEBF7" } };

    // ── ROW 4: DATE ────────────────────────────────────────
    const dateStr = selectedDate ? `At  ${formatDate(selectedDate)}` : "";
    const dateRow = sheet.addRow([dateStr]);
    mergeRow(dateRow.number);
    dateRow.font      = { italic: true, size: 10 };
    dateRow.alignment = { horizontal: "center" };
    dateRow.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "EAF1FB" } };

    // ── ROW 5: PRINTED DATE ────────────────────────────────
    const printedRow = sheet.addRow([`Printed Date : ${new Date().toLocaleString()}`]);
    mergeRow(printedRow.number);
    printedRow.font      = { italic: true, size: 9 };
    printedRow.alignment = { horizontal: "left", indent: 1 };
    printedRow.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "F2F6FC" } };

    sheet.addRow([]);

    const groups = [];
    const groupIndex = {};

    data.forEach((item) => {
        const key = item.headerId;
        if (groupIndex[key] === undefined) {
            groupIndex[key] = groups.length;
            groups.push({ headerName: item.headerName, rows: [] });
        }
        groups[groupIndex[key]].rows.push(item);
    });

    let grandDebit  = 0;
    let grandCredit = 0;

    groups.forEach((group) => {
        const ghLabelRow = sheet.addRow([group.headerName]);
        mergeRow(ghLabelRow.number);
        ghLabelRow.font      = { bold: true, size: 12, underline: true, color: { argb: C.DARK_TEXT } };
        ghLabelRow.height    = 20;
        ghLabelRow.alignment = { horizontal: "left", vertical: "middle", indent: 1 };
        ghLabelRow.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: C.GROUP_LABEL_BG } };

        const colHdrRow = sheet.addRow(["Account", "Code", "Debit", "Credit"]);
        colHdrRow.eachCell((cell) => {
            cell.font      = { bold: true, color: { argb: C.WHITE } };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: C.GROUP_BG } };
            applyBorder(cell);
        });
        colHdrRow.height = 16;

        let groupDebit  = 0;
        let groupCredit = 0;

        group.rows.forEach((item, idx) => {
            const dr = sheet.addRow([
                item.subACName   ?? "",
                item.subAcNumber ?? "",
                item.debitAmount  ?? 0,
                item.creditAmount ?? 0,
            ]);

            groupDebit  += item.debitAmount  ?? 0;
            groupCredit += item.creditAmount ?? 0;

            dr.eachCell((cell, colNum) => {
                applyBorder(cell);
                cell.alignment = { vertical: "middle" };

                if (colNum === 2) {
                    cell.alignment = { horizontal: "center", vertical: "middle" };
                }
                if (colNum === 3 || colNum === 4) {
                    applyCurrency(cell);
                }
                if (idx % 2 === 0) {
                    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.ALT_ROW } };
                }
            });
        });

        grandDebit  += groupDebit;
        grandCredit += groupCredit;

        const subTotalRow = sheet.addRow(["Sub Total", "", groupDebit, groupCredit]);
        subTotalRow.eachCell((cell, colNum) => {
            cell.font  = { bold: true };
            cell.fill  = { type: "pattern", pattern: "solid", fgColor: { argb: C.SUMMARY_BG } };
            applyBorder(cell, DOUBLE_BOTTOM);
            if (colNum === 3 || colNum === 4) {
                applyCurrency(cell);
                cell.font = { bold: true };
            } else {
                cell.alignment = { vertical: "middle" };
            }
        });

        sheet.addRow([]);
    });

    const grandRow = sheet.addRow(["Total", "", grandDebit, grandCredit]);
    grandRow.height = 20;
    grandRow.eachCell((cell, colNum) => {
        cell.font  = { bold: true, size: 11, color: { argb: C.WHITE } };
        cell.fill  = { type: "pattern", pattern: "solid", fgColor: { argb: C.HEADER_BG } };
        applyBorder(cell, DOUBLE_BOTTOM);
        if (colNum === 3 || colNum === 4) {
            applyCurrency(cell);
            cell.font = { bold: true, size: 11, color: { argb: C.WHITE } };
        } else {
            cell.alignment = { vertical: "middle" };
        }
    });

    sheet.views = [{ state: "frozen", ySplit: 6 }];

    if (loginPlaceId === "1") {
        const password = await protectSheetWithRandomPassword(sheet, 12);
        console.log(`Sheet protected with password: ${password}`);
    }

    const now       = new Date();
    const dateSufx  = now.toISOString().slice(0, 10);
    const timeSufx  = now.toLocaleTimeString("en-US", { hour12: false }).replace(/:/g, "-");
    const safeNm    = (branchName || "branch").replace(/\s+/g, "_");
    const fileName  = `TrailBalance-${safeNm}-${dateSufx}_${timeSufx}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob   = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, fileName);
}
